// SBIS Consulting — Cashflow mesečni izveštaj
import { state } from './state.js';
import { sb } from './supabase.js';
import { fNum } from './utils.js';
import { toast, showLoading, hideLoading } from './ui.js';
import XLSX from 'xlsx-js-style';

let lastCashflow = null;

const MONTHS_SR = ['Januar','Februar','Mart','April','Maj','Jun','Jul','Avgust','Septembar','Oktobar','Novembar','Decembar'];

export function initCashflow() {
  const yearSel = document.getElementById('cfYear');
  if (!yearSel) return;
  const curYear = new Date().getFullYear();
  for (let y = curYear; y >= curYear - 3; y--) {
    const o = document.createElement('option'); o.value = y; o.textContent = y;
    yearSel.appendChild(o);
  }
  // Default to previous month
  const now = new Date();
  const prevMonth = now.getMonth(); // 0-indexed, so this is previous month (1-indexed)
  const prevYear = prevMonth === 0 ? curYear - 1 : curYear;
  document.getElementById('cfMonth').value = prevMonth === 0 ? 12 : prevMonth;
  document.getElementById('cfYear').value = prevYear;
}

export async function generateCashflow() {
  if (!state.CU) { toast('Niste prijavljeni', 'err'); return; }
  const month = parseInt(document.getElementById('cfMonth').value);
  const year = parseInt(document.getElementById('cfYear').value);
  const kurs = parseFloat(document.getElementById('cfKurs').value) || 108;

  showLoading();
  try {
    const { data, error } = await sb.rpc('calculate_cashflow', {
      p_user_id: state.CU.id, p_year: year, p_month: month, p_kurs: kurs
    });
    if (error) throw error;
    lastCashflow = data;
    renderCashflow(data);
    document.getElementById('cfSaveBtn').style.display = '';
    document.getElementById('cfExpBtn').style.display = '';
    toast('Cashflow generisan za ' + MONTHS_SR[month - 1] + ' ' + year);
  } catch (e) { toast('Greška: ' + (e.message || e), 'err'); }
  hideLoading();
}

export async function saveCashflow() {
  if (!lastCashflow || !state.CU) return;
  const month = parseInt(document.getElementById('cfMonth').value);
  const year = parseInt(document.getElementById('cfYear').value);
  const kurs = parseFloat(document.getElementById('cfKurs').value) || 108;

  showLoading();
  try {
    const { error } = await sb.rpc('save_cashflow', {
      p_user_id: state.CU.id, p_year: year, p_month: month,
      p_kurs: kurs, p_data: lastCashflow
    });
    if (error) throw error;
    toast('Cashflow sačuvan za ' + MONTHS_SR[month - 1] + ' ' + year);
  } catch (e) { toast('Greška: ' + (e.message || e), 'err'); }
  hideLoading();
}

function renderCashflow(cf) {
  const out = document.getElementById('cfOutput');
  out.textContent = '';
  const kurs = cf.kurs || 108;

  // Ensure nested containers exist and mutate cf in place (edits flow back via lastCashflow reference)
  cf.opening = { rsd: 0, usd: 0, ...(cf.opening || {}) };
  cf.income = { customer_collection_rsd: 0, customer_collection_usd: 0, tax_refund: 0, recapitalization: 0, ...(cf.income || {}) };
  cf.expenses = { payment_suppliers: 0, suppliers_advance: 0, fix_asset_advance: 0, salaries: 0, import_taxes: 0, bank_commission: 0, gov_taxes: 0, others: 0, ...(cf.expenses || {}) };
  cf.taxes = { monthly_vat: 0, retained_vat: 0, ...(cf.taxes || {}) };

  // Normalizuj: *_usd polja su istorijski RSD — prebaci u *_rsd i zeruj *_usd
  if (cf.opening.usd) { cf.opening.rsd = (cf.opening.rsd || 0) + cf.opening.usd; cf.opening.usd = 0; }
  if (cf.income.customer_collection_usd) { cf.income.customer_collection_rsd = (cf.income.customer_collection_rsd || 0) + cf.income.customer_collection_usd; cf.income.customer_collection_usd = 0; }

  const o = cf.opening, inc = cf.income, exp = cf.expenses, tax = cf.taxes;
  const monthName = MONTHS_SR[(cf.month || 1) - 1] + ' ' + (cf.year || '');

  // Parse srpski formatiran broj: "173.967.470,50" → 173967470.5; "-8.000.000" → -8000000
  function parseNum(s) {
    if (s == null) return 0;
    const clean = String(s).replace(/[.\s\u00a0]/g, '').replace(',', '.').replace(/[^\d.\-]/g, '');
    return parseFloat(clean) || 0;
  }

  // Commit izmene u cf preko dot-path i ponovo renderuj
  function commitEdit(path, sign, rawText) {
    const num = parseNum(rawText);
    const value = sign < 0 ? Math.abs(num) : num;
    const parts = path.split('.');
    let obj = cf;
    for (let i = 0; i < parts.length - 1; i++) obj = obj[parts[i]];
    obj[parts[parts.length - 1]] = value;
    renderCashflow(cf);
  }

  // Helper: create a row
  function row(label, din, dinUsd, usd, totUsd, opts = {}) {
    const tr = document.createElement('tr');
    if (opts.header) tr.style.cssText = 'background:var(--surface);font-weight:700';
    if (opts.total) tr.style.cssText = 'background:var(--raised);font-weight:700;border-top:2px solid var(--acc)';
    if (opts.subtotal) tr.style.cssText = 'font-weight:600;border-top:1px solid var(--border)';
    if (opts.section) tr.style.cssText = 'background:var(--surface);font-weight:700;color:var(--acc)';

    const tdL = document.createElement('td'); tdL.style.cssText = 'padding:6px 10px;font-size:15px';
    if (opts.indent) tdL.style.paddingLeft = '24px';
    if (opts.prefix) { const sp = document.createElement('span'); sp.style.cssText = 'color:var(--tx3);margin-right:6px;font-size:12px'; sp.textContent = opts.prefix; tdL.appendChild(sp); }
    tdL.appendChild(document.createTextNode(label));

    // Helper: napravi numeričku ćeliju, opciono editabilnu sa konverzijom u RSD
    // toRsdFactor: faktor kojim se pomnoži unesena vrednost da se dobije RSD (1 za RSD, kurs za USD kolone)
    function numCell(val, baseStyle, toRsdFactor) {
      const td = document.createElement('td');
      td.style.cssText = 'padding:6px 8px;text-align:right;font-family:var(--mn);font-size:15px' + (baseStyle || '');
      if (val !== null && val !== undefined) {
        td.textContent = fNum(val);
        if (val < 0) td.style.color = 'var(--dng)';
      }
      if (opts.editPath) {
        td.contentEditable = 'true';
        td.spellcheck = false;
        td.style.cursor = 'text';
        td.style.outline = 'none';
        td.style.borderBottom = '1px dashed rgba(201,168,76,.25)';
        td.title = 'Klikni za izmenu, Enter/Tab potvrda, Esc otkazi';
        if (val === null || val === undefined) td.textContent = '';
        td.addEventListener('focus', () => {
          td.style.background = 'rgba(201,168,76,.15)';
          const r = document.createRange(); r.selectNodeContents(td);
          const sel = window.getSelection(); sel.removeAllRanges(); sel.addRange(r);
        });
        td.addEventListener('blur', () => {
          td.style.background = '';
          const newVal = parseNum(td.textContent);
          const currentVal = (val === null || val === undefined) ? 0 : val;
          if (Math.abs(newVal - currentVal) > 0.005) {
            const rsdValue = newVal * (toRsdFactor || 1);
            commitEdit(opts.editPath, opts.sign || 1, String(rsdValue));
          } else {
            td.textContent = (val === null || val === undefined) ? '' : fNum(val);
          }
        });
        td.addEventListener('keydown', e => {
          if (e.key === 'Enter' || e.key === 'Tab') { e.preventDefault(); td.blur(); }
          if (e.key === 'Escape') { td.textContent = (val === null || val === undefined) ? '' : fNum(val); td.blur(); }
        });
      }
      return td;
    }

    const tdDin = numCell(din, din > 0 ? ';color:var(--tx)' : '', 1);
    const tdDinUsd = numCell(dinUsd, ';color:var(--tx2)', kurs);
    const tdUsd = numCell(usd, ';color:var(--acc)', kurs);
    const tdTot = numCell(totUsd, ';font-weight:600', kurs);
    if (totUsd !== null && totUsd !== undefined && totUsd > 0) tdTot.style.color = 'var(--suc)';

    tr.appendChild(tdL); tr.appendChild(tdDin); tr.appendChild(tdDinUsd); tr.appendChild(tdUsd); tr.appendChild(tdTot);
    return tr;
  }

  // Card wrapper
  const card = document.createElement('div'); card.className = 'card'; card.style.cssText = 'padding:0;overflow-x:auto';

  // Title
  const title = document.createElement('div'); title.style.cssText = 'padding:16px 20px;border-bottom:2px solid var(--acc);display:flex;justify-content:space-between;align-items:center';
  const h = document.createElement('div');
  const h2 = document.createElement('h2'); h2.style.cssText = 'font-size:19px;color:var(--acc)'; h2.textContent = 'CASH FLOW \u2014 ' + monthName;
  const sub = document.createElement('div'); sub.style.cssText = 'font-size:13px;color:var(--tx3);margin-top:2px'; sub.textContent = 'ECOR SERBIA / SBIS Consulting';
  h.appendChild(h2); h.appendChild(sub);
  const kursInfo = document.createElement('div'); kursInfo.style.cssText = 'font-family:var(--mn);font-size:14px;color:var(--tx3)'; kursInfo.textContent = 'ER: ' + fNum(kurs);
  title.appendChild(h); title.appendChild(kursInfo);
  card.appendChild(title);

  // Hint za edit
  const hint = document.createElement('div');
  hint.style.cssText = 'padding:8px 20px;background:rgba(201,168,76,.06);border-bottom:1px solid var(--border);font-size:12px;color:var(--tx3)';
  hint.textContent = 'Klikni na bilo koju brojčanu ćeliju (iscrtkana donja linija) da izmeniš iznos. Enter ili Tab za potvrdu, Esc za otkazivanje. Izmene se čuvaju tek kad klikneš "Sačuvaj".';
  card.appendChild(hint);

  // Table
  const tbl = document.createElement('table'); tbl.style.cssText = 'width:100%;border-collapse:collapse;table-layout:fixed';
  const cfCg = document.createElement('colgroup');
  [{w:'30%'},{w:'18%'},{w:'18%'},{w:'16%'},{w:'18%'}].forEach(c => { const col = document.createElement('col'); col.style.width = c.w; cfCg.appendChild(col); });
  tbl.appendChild(cfCg);

  // Header
  const thead = document.createElement('thead');
  const htr = document.createElement('tr'); htr.style.cssText = 'background:var(--raised);border-bottom:1px solid var(--border)';
  ['', 'RSD', 'RSD \u2192 USD', 'USD', 'Tot. USD'].forEach((h, i) => {
    const th = document.createElement('th'); th.textContent = h;
    th.style.cssText = 'padding:8px 10px;font-size:12px;text-transform:uppercase;letter-spacing:.5px;color:var(--tx3)' + (i > 0 ? ';text-align:right' : ';text-align:left');
    htr.appendChild(th);
  });
  thead.appendChild(htr); tbl.appendChild(thead);

  const tbody = document.createElement('tbody');

  // Polja *_usd i recapitalization su istorijski unošena kao RSD — mergovana u *_rsd pri normalizaciji iznad.
  const openDin = o.rsd || 0;
  const openDinUsd = openDin / kurs;
  tbody.appendChild(row('BALANCE', openDin, openDinUsd, null, openDinUsd, { header: true, editPath: 'opening.rsd' }));

  // INCOME
  tbody.appendChild(row('INCOME', null, null, null, null, { section: true, prefix: '( + )' }));
  const ccDin = inc.customer_collection_rsd || 0;
  const ccDinUsd = ccDin / kurs;
  tbody.appendChild(row('Customer Collection', ccDin, ccDinUsd, null, ccDinUsd, { indent: true, editPath: 'income.customer_collection_rsd' }));
  const recapDin = inc.recapitalization || 0;
  const recapDinUsd = recapDin / kurs;
  tbody.appendChild(row('Recapitalization', recapDin, recapDinUsd, null, recapDinUsd, { indent: true, editPath: 'income.recapitalization' }));
  const trUsd = inc.tax_refund / kurs;
  tbody.appendChild(row('Tax Refund & Other', inc.tax_refund, trUsd, null, trUsd, { indent: true, editPath: 'income.tax_refund' }));

  const totalIncDin = ccDin + recapDin + inc.tax_refund;
  const totalIncDinUsd = totalIncDin / kurs;
  tbody.appendChild(row('Total Income', totalIncDin, totalIncDinUsd, null, totalIncDinUsd, { subtotal: true }));

  // EXPENSES — signs su -1 jer su u bazi pozitivni ali se prikazuju negativno
  tbody.appendChild(row('EXPENSES', null, null, null, null, { section: true, prefix: '( - )' }));
  const psUsd = -exp.payment_suppliers / kurs;
  tbody.appendChild(row('Payment to Suppliers', -exp.payment_suppliers, psUsd, null, psUsd, { indent: true, editPath: 'expenses.payment_suppliers', sign: -1 }));
  const saUsd = -(exp.suppliers_advance || 0) / kurs;
  tbody.appendChild(row('Suppliers Advance', -(exp.suppliers_advance || 0), saUsd, null, saUsd, { indent: true, editPath: 'expenses.suppliers_advance', sign: -1 }));
  const faUsd = -(exp.fix_asset_advance || 0) / kurs;
  tbody.appendChild(row('Fix Asset Advance', -(exp.fix_asset_advance || 0), faUsd, null, faUsd, { indent: true, editPath: 'expenses.fix_asset_advance', sign: -1 }));
  const salUsd = -exp.salaries / kurs;
  tbody.appendChild(row('Salaries', -exp.salaries, salUsd, null, salUsd, { indent: true, editPath: 'expenses.salaries', sign: -1 }));
  const itUsd = -exp.import_taxes / kurs;
  tbody.appendChild(row('Import/Export Taxes', -exp.import_taxes, itUsd, null, itUsd, { indent: true, editPath: 'expenses.import_taxes', sign: -1 }));
  const bcUsd = -exp.bank_commission / kurs;
  tbody.appendChild(row('Bank Commission', -exp.bank_commission, bcUsd, null, bcUsd, { indent: true, editPath: 'expenses.bank_commission', sign: -1 }));
  const gtUsd = -exp.gov_taxes / kurs;
  tbody.appendChild(row('Government Taxes & Contributions', -exp.gov_taxes, gtUsd, null, gtUsd, { indent: true, editPath: 'expenses.gov_taxes', sign: -1 }));
  const otUsd = -exp.others / kurs;
  tbody.appendChild(row('Others', -exp.others, otUsd, null, otUsd, { indent: true, editPath: 'expenses.others', sign: -1 }));

  const totalExpDin = -(exp.payment_suppliers + (exp.suppliers_advance || 0) + (exp.fix_asset_advance || 0) + exp.salaries + exp.import_taxes + exp.bank_commission + exp.gov_taxes + exp.others);
  const totalExpDinUsd = totalExpDin / kurs;
  tbody.appendChild(row('Total Expenses', totalExpDin, totalExpDinUsd, null, totalExpDinUsd, { subtotal: true }));

  // FINAL BALANCE
  const finalDin = totalIncDin + totalExpDin;
  const finalTotUsd = finalDin / kurs;
  tbody.appendChild(row('FINAL BALANCE OF CASH MOVEMENTS', finalDin, finalTotUsd, null, finalTotUsd, { total: true }));

  // MONTHLY TAXES
  const emptyRow = document.createElement('tr');
  const emptyTd = document.createElement('td'); emptyTd.colSpan = 5; emptyTd.style.height = '8px'; emptyRow.appendChild(emptyTd);
  tbody.appendChild(emptyRow);

  const vatUsd = tax.monthly_vat / kurs;
  tbody.appendChild(row('Monthly VAT', tax.monthly_vat, vatUsd, null, vatUsd, { indent: true, editPath: 'taxes.monthly_vat' }));
  const retUsd = tax.retained_vat / kurs;
  tbody.appendChild(row('Retained VAT & Income Tax', tax.retained_vat, retUsd, null, retUsd, { indent: true, editPath: 'taxes.retained_vat' }));
  const totalTaxUsd = vatUsd + retUsd;
  tbody.appendChild(row('MONTHLY TAXES', null, null, null, totalTaxUsd, { subtotal: true, prefix: '( - )' }));

  // NET CASH = final balance MINUS taxes
  const netCash = finalTotUsd - totalTaxUsd;
  tbody.appendChild(row('NET CASH', null, null, null, netCash, { total: true, prefix: '( = )' }));

  tbl.appendChild(tbody); card.appendChild(tbl); out.appendChild(card);
}

export function exportCashflowXlsx() {
  if (!lastCashflow) { toast('Generišite cashflow prvo', 'err'); return; }
  const cf = lastCashflow;
  const kurs = cf.kurs || 108;
  const o = { rsd: 0, usd: 0, ...(cf.opening || {}) };
  const inc = { customer_collection_rsd: 0, customer_collection_usd: 0, tax_refund: 0, recapitalization: 0, ...(cf.income || {}) };
  const exp = { payment_suppliers: 0, suppliers_advance: 0, fix_asset_advance: 0, salaries: 0, import_taxes: 0, bank_commission: 0, gov_taxes: 0, others: 0, ...(cf.expenses || {}) };
  const tax = { monthly_vat: 0, retained_vat: 0, ...(cf.taxes || {}) };
  const monthName = MONTHS_SR[(cf.month || 1) - 1] + ' ' + (cf.year || '');

  const hS = { font: { bold: true, color: { rgb: 'FFFFFF' }, sz: 11 }, fill: { fgColor: { rgb: '0F1923' } }, alignment: { horizontal: 'center' } };
  const nS = { font: { sz: 10 }, numFmt: '#,##0.000' };
  const bS = { font: { bold: true, sz: 11 }, numFmt: '#,##0.000', fill: { fgColor: { rgb: 'FEF08A' } } };
  const tS = { font: { bold: true, sz: 12, color: { rgb: 'FFFFFF' } }, fill: { fgColor: { rgb: '0F1923' } }, numFmt: '#,##0.000' };
  function c(v, s) { return { v, s, t: typeof v === 'number' ? 'n' : 's' }; }

  const rows = [];
  rows.push([c('ECOR SERBIA', hS), c('', hS), c('', hS), c('', hS), c('', hS), c('', hS)]);
  rows.push([c('CASH FLOW — ' + monthName, { font: { bold: true, sz: 12 } }), c('', {}), c('', {}), c('', {}), c('', {}), c('', {})]);
  rows.push([c('', {}), c('', {}), c('RSD', hS), c('RSD→USD', hS), c('USD', hS), c('Tot. USD', hS)]);

  // *_usd i recapitalization su istorijski RSD — mergovano u DIN kolonu
  const openDin = (o.rsd || 0) + (o.usd || 0);
  const openDinUsd = openDin / kurs;
  rows.push([c('', {}), c('BALANCE', bS), c(openDin, bS), c(openDinUsd, bS), c(null, bS), c(openDinUsd, bS)]);
  rows.push([c('( + )', { font: { bold: true } }), c('INCOME', { font: { bold: true } }), c('', {}), c('', {}), c('', {}), c('', {})]);

  const ccDin = (inc.customer_collection_rsd || 0) + (inc.customer_collection_usd || 0);
  const ccDinUsd = ccDin / kurs;
  rows.push([c('', {}), c('Customer Collection', nS), c(ccDin, nS), c(ccDinUsd, nS), c(null, nS), c(ccDinUsd, nS)]);
  const recapDin = inc.recapitalization || 0;
  const recapDinUsd = recapDin / kurs;
  rows.push([c('', {}), c('Recapitalization', nS), c(recapDin, nS), c(recapDinUsd, nS), c(null, nS), c(recapDinUsd, nS)]);
  rows.push([c('', {}), c('Tax Refund & Other', nS), c(inc.tax_refund, nS), c(inc.tax_refund / kurs, nS), c(null, nS), c(inc.tax_refund / kurs, nS)]);

  const totalIncDin = ccDin + recapDin + inc.tax_refund;
  rows.push([c('', {}), c('Total Income', bS), c(totalIncDin, bS), c(totalIncDin / kurs, bS), c(null, bS), c(totalIncDin / kurs, bS)]);

  rows.push([c('( - )', { font: { bold: true } }), c('EXPENSES', { font: { bold: true } }), c('', {}), c('', {}), c('', {}), c('', {})]);
  rows.push([c('', {}), c('Payment to Suppliers', nS), c(-exp.payment_suppliers, nS), c(-exp.payment_suppliers / kurs, nS), c(null, nS), c(-exp.payment_suppliers / kurs, nS)]);
  if ((exp.suppliers_advance || 0) > 0) rows.push([c('', {}), c('Suppliers Advance', nS), c(-(exp.suppliers_advance||0), nS), c(-(exp.suppliers_advance||0) / kurs, nS), c(null, nS), c(-(exp.suppliers_advance||0) / kurs, nS)]);
  if ((exp.fix_asset_advance || 0) > 0) rows.push([c('', {}), c('Fix Asset Advance', nS), c(-(exp.fix_asset_advance||0), nS), c(-(exp.fix_asset_advance||0) / kurs, nS), c(null, nS), c(-(exp.fix_asset_advance||0) / kurs, nS)]);
  rows.push([c('', {}), c('Salaries', nS), c(-exp.salaries, nS), c(-exp.salaries / kurs, nS), c(null, nS), c(-exp.salaries / kurs, nS)]);
  if (exp.import_taxes > 0) rows.push([c('', {}), c('Import/Export Taxes', nS), c(-exp.import_taxes, nS), c(-exp.import_taxes / kurs, nS), c(null, nS), c(-exp.import_taxes / kurs, nS)]);
  if (exp.bank_commission > 0) rows.push([c('', {}), c('Bank Commission', nS), c(-exp.bank_commission, nS), c(-exp.bank_commission / kurs, nS), c(null, nS), c(-exp.bank_commission / kurs, nS)]);
  rows.push([c('', {}), c('Gov. Taxes & Contributions', nS), c(-exp.gov_taxes, nS), c(-exp.gov_taxes / kurs, nS), c(null, nS), c(-exp.gov_taxes / kurs, nS)]);
  if (exp.others > 0) rows.push([c('', {}), c('Others', nS), c(-exp.others, nS), c(-exp.others / kurs, nS), c(null, nS), c(-exp.others / kurs, nS)]);

  const totalExpDin = -(exp.payment_suppliers + (exp.suppliers_advance||0) + (exp.fix_asset_advance||0) + exp.salaries + exp.import_taxes + exp.bank_commission + exp.gov_taxes + exp.others);
  rows.push([c('', {}), c('Total Expenses', bS), c(totalExpDin, bS), c(totalExpDin / kurs, bS), c(null, bS), c(totalExpDin / kurs, bS)]);

  const finalDin = totalIncDin + totalExpDin;
  const finalTotUsd = finalDin / kurs;
  rows.push([c('', {}), c('FINAL BALANCE', tS), c(finalDin, tS), c(finalTotUsd, tS), c(null, tS), c(finalTotUsd, tS)]);
  rows.push([c('', {}), c('', {}), c('', {}), c('', {}), c('', {}), c('', {})]);

  if (tax.monthly_vat > 0) rows.push([c('', {}), c('Monthly VAT', nS), c(tax.monthly_vat, nS), c(tax.monthly_vat / kurs, nS), c(null, nS), c(tax.monthly_vat / kurs, nS)]);
  rows.push([c('', {}), c('Retained VAT & Income Tax', nS), c(tax.retained_vat, nS), c(tax.retained_vat / kurs, nS), c(null, nS), c(tax.retained_vat / kurs, nS)]);

  const netCash = finalTotUsd - (tax.monthly_vat / kurs) - (tax.retained_vat / kurs);
  rows.push([c('( = )', { font: { bold: true } }), c('NET CASH', tS), c(null, tS), c(null, tS), c(null, tS), c(netCash, tS)]);
  rows.push([c('', {}), c('', {}), c('', {}), c('', {}), c('', {}), c('', {})]);
  rows.push([c('ER', { font: { bold: true } }), c(kurs, { font: { bold: true }, numFmt: '#,##0.0000' }), c('', {}), c('', {}), c('', {}), c('', {})]);

  const ws = XLSX.utils.aoa_to_sheet(rows.map(r => r.map(c => c.v)));
  ws['!cols'] = [{ wch: 8 }, { wch: 32 }, { wch: 16 }, { wch: 14 }, { wch: 14 }, { wch: 14 }];
  rows.forEach((row, ri) => { row.forEach((cell, ci) => { const addr = XLSX.utils.encode_cell({ r: ri, c: ci }); if (ws[addr]) ws[addr].s = cell.s; }); });

  const wb = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(wb, ws, 'Cash Flow');
  XLSX.writeFile(wb, 'cashflow_' + monthName.replace(' ', '_') + '.xlsx', { bookSST: false, cellStyles: true });
  toast('Excel eksportovan');
  if (state.CU) sb.rpc('log_audit', { p_user_id: state.CU.id, p_action: 'export_excel', p_entity_type: 'cashflow', p_entity_name: 'cashflow_' + monthName }).then(null, e => console.error('SBIS:', e));
}
